/**
 * rehypeSpacing.js - Rehype plugin to fix spacing around bold/italic elements
 * 
 * LLMs often generate markdown like "lack**context**" without proper spacing.
 * This plugin runs AFTER markdown is parsed into HTML AST, where we can
 * unambiguously identify opening vs closing tags.
 * 
 * The plugin walks the AST and:
 * - Adds space before <strong>/<em> if preceded by a word character
 * - Adds space after </strong>/</em> if followed by a word character
 * 
 * @example
 * // In ReactMarkdown:
 * <ReactMarkdown rehypePlugins={[rehypeSpacing]}>
 *   {content}
 * </ReactMarkdown>
 */

import { visit } from 'unist-util-visit';

/**
 * Check if a character is a word character (alphanumeric)
 */
const isWordChar = (char) => {
  if (!char) return false;
  return /\w/.test(char);
};

/**
 * Rehype plugin to add spacing around strong/em elements
 */
export default function rehypeSpacing() {
  return (tree) => {
    visit(tree, 'element', (node, index, parent) => {
      // Only process strong and em elements
      if (node.tagName !== 'strong' && node.tagName !== 'em') {
        return;
      }
      
      if (!parent || typeof index !== 'number') {
        return;
      }
      
      const siblings = parent.children;
      
      // Check previous sibling for spacing before opening tag
      if (index > 0) {
        const prevSibling = siblings[index - 1];
        if (prevSibling && prevSibling.type === 'text' && prevSibling.value) {
          const lastChar = prevSibling.value.slice(-1);
          if (isWordChar(lastChar)) {
            // Add space at the end of previous text node
            prevSibling.value = prevSibling.value + ' ';
          }
        }
      }
      
      // Check next sibling for spacing after closing tag
      if (index < siblings.length - 1) {
        const nextSibling = siblings[index + 1];
        if (nextSibling && nextSibling.type === 'text' && nextSibling.value) {
          const firstChar = nextSibling.value.charAt(0);
          if (isWordChar(firstChar)) {
            // Add space at the beginning of next text node
            nextSibling.value = ' ' + nextSibling.value;
          }
        }
      }
    });
  };
}
